import type { SportDefinition } from '@fsp/sports-core';
import { footballCalendar } from './calendar';
import { footballChallenge } from './challenge';
import { footballDisplay } from './display';
import { footballLeagueNameWords } from './league-names';
import { rosterPresets, scoringPresets } from './presets';
import { footballScoringLayout, footballScoringSetup } from './scoring-layout';
import { footballStats } from './stats';
import { nflTeams } from './teams';

export const football: SportDefinition = Object.freeze({
  id: 'football',
  name: 'Football',
  icon: 'football',
  proLeagueName: 'NFL',
  calendar: footballCalendar,
  positions: [
    { id: 'QB', name: 'Quarterback', group: 'offense', color: 'pos-qb', sortOrder: 1 },
    { id: 'RB', name: 'Running Back', group: 'offense', color: 'pos-rb', sortOrder: 2 },
    { id: 'WR', name: 'Wide Receiver', group: 'offense', color: 'pos-wr', sortOrder: 3 },
    { id: 'TE', name: 'Tight End', group: 'offense', color: 'pos-te', sortOrder: 4 },
    { id: 'K', name: 'Kicker', group: 'special', color: 'pos-k', sortOrder: 5 },
    { id: 'DST', name: 'Team Defense', group: 'defense', color: 'pos-dst', sortOrder: 6 },
    // DEFENSIVE PLAYERS (Q11, PLAN-2026-10-18-defensive-players): Yahoo's and
    // Sleeper's three, a league starting them in place of a team's defense.
    { id: 'DL', name: 'Defensive Lineman', group: 'idp', color: 'pos-dl', sortOrder: 7 },
    { id: 'LB', name: 'Linebacker', group: 'idp', color: 'pos-lb', sortOrder: 8 },
    { id: 'DB', name: 'Defensive Back', group: 'idp', color: 'pos-db', sortOrder: 9 },
  ],
  slots: [
    { id: 'QB', name: 'QB', eligible: ['QB'], kind: 'starter', sortOrder: 1 },
    { id: 'RB', name: 'RB', eligible: ['RB'], kind: 'starter', sortOrder: 2 },
    { id: 'WR', name: 'WR', eligible: ['WR'], kind: 'starter', sortOrder: 3 },
    { id: 'TE', name: 'TE', eligible: ['TE'], kind: 'starter', sortOrder: 4 },
    { id: 'FLEX', name: 'Flex', eligible: ['RB', 'WR', 'TE'], kind: 'starter', sortOrder: 5 },
    { id: 'WRT', name: 'W/R/T', eligible: ['RB', 'WR', 'TE'], kind: 'starter', sortOrder: 6 },
    {
      id: 'SFLEX',
      name: 'Superflex',
      eligible: ['QB', 'RB', 'WR', 'TE'],
      kind: 'starter',
      sortOrder: 7,
    },
    { id: 'K', name: 'K', eligible: ['K'], kind: 'starter', sortOrder: 8 },
    { id: 'DST', name: 'DEF', eligible: ['DST'], kind: 'starter', sortOrder: 9 },
    { id: 'DL', name: 'DL', eligible: ['DL'], kind: 'starter', sortOrder: 10 },
    { id: 'LB', name: 'LB', eligible: ['LB'], kind: 'starter', sortOrder: 11 },
    { id: 'DB', name: 'DB', eligible: ['DB'], kind: 'starter', sortOrder: 12 },
    // The flex over the three, as Sleeper's IDP flex and ESPN's DP.
    { id: 'IDP', name: 'D Flex', eligible: ['DL', 'LB', 'DB'], kind: 'starter', sortOrder: 13 },
    { id: 'BN', name: 'Bench', eligible: [], kind: 'bench', sortOrder: 14 },
    { id: 'IR', name: 'IR', eligible: [], kind: 'reserve', sortOrder: 15 },
    { id: 'TAXI', name: 'Taxi', eligible: [], kind: 'taxi', sortOrder: 16 },
  ],
  rosterPresets,
  injuryStatuses: [
    { id: 'Q', name: 'Questionable', severity: 'warn', reserveEligible: false, lasts: 'game' },
    { id: 'D', name: 'Doubtful', severity: 'likely-out', reserveEligible: false, lasts: 'game' },
    { id: 'O', name: 'Out', severity: 'out', reserveEligible: true, lasts: 'game' },
    {
      id: 'IR',
      name: 'Injured Reserve',
      severity: 'out',
      reserveEligible: true,
      lasts: 'standing',
    },
    {
      id: 'PUP',
      name: 'Physically Unable to Perform',
      severity: 'out',
      reserveEligible: true,
      lasts: 'standing',
    },
    {
      id: 'NFI',
      name: 'Non-Football Injury',
      severity: 'out',
      reserveEligible: true,
      lasts: 'standing',
    },
    { id: 'SUS', name: 'Suspended', severity: 'out', reserveEligible: true, lasts: 'standing' },
    { id: 'NA', name: 'Inactive', severity: 'out', reserveEligible: false, lasts: 'standing' },
  ],
  stats: footballStats,
  scoringPresets,
  categoryPresets: [],
  scoringLayout: footballScoringLayout,
  scoringSetup: footballScoringSetup,
  supportedFormats: ['h2h-points', 'points-total', 'all-play', 'best-ball', 'elimination'],
  lineupPeriods: ['weekly'],
  defaultLineupPeriod: 'weekly',
  defaultLockRule: 'per-player-game',
  proTeams: nflTeams,
  display: footballDisplay,
  // E8's usage: a receiver's share of his club's targets, and his air yards
  // a game beside it.
  usage: { share: 'rec_tgt', perGame: 'air_yd' },
  // The create form's name ideas: Sundays, Hail Marys and the rest (B-430).
  leagueNameWords: footballLeagueNameWords,
  // The weekly challenge's positions and categories (Q9).
  weeklyChallenge: footballChallenge,
  researchModules: [
    {
      id: 'usage',
      name: 'Usage',
      placement: 'player-card',
      requiredStats: ['snap_pct', 'target_share', 'rz_touches'],
      component: 'football/UsagePanel',
    },
    {
      id: 'matchup-strength',
      name: 'Matchup',
      placement: 'player-card',
      requiredStats: ['opp_rank_vs_pos'],
      component: 'football/MatchupPanel',
    },
  ],
} satisfies SportDefinition);
